import React from 'react';
import { Box } from '@mui/material';
import { useRecoilState, useRecoilValue } from 'recoil';
import { endPoints } from 'api/endpoints';
import axios from 'api/request';
import { useMutation } from 'react-query';
import Select from 'pages/reference-implementation/components/select';
import { componentGroupsState, OrderPayload } from 'pages/reference-implementation/provisioning/RI003AWS/store';
import { InputLabel } from 'pages/reference-implementation/components/input-label';
import { selectedEnv } from '../../utils/constant';
import { getComponentsFromSchema } from '../../utils';

export const ApplicationRegion = () => {
    const [isTouched, setIsTouched] = React.useState(false);
    const [order, setOrder] = useRecoilState(OrderPayload);
    const componentGroups = useRecoilValue(componentGroupsState);
    const [options, setOptions] = React.useState([]);
    const regionApi = useMutation(() => axios.get(`${endPoints.aws.Ec2.applicationRegion}`));

    const setApplicationRegion = (appRegion) => {
        setOrder({
            ...order,
            appRegion,
            program: null,
            resourceMgmt: {
                [selectedEnv]: {
                    components: getComponentsFromSchema({ componentGroups, particularPrompt: ['mandatoryComponents'] })?.[0]?.components || [],
                },
            },
        });
    };

    const seldValueExistsInOptions = (regionValue) => options.find((option) => regionValue === option?.value);

    const handleOnSelectChange = (selected) => {
        if (selected && seldValueExistsInOptions(selected?.value)) {
            setApplicationRegion(selected?.value);
        }
    };

    const getOptionsBasedOnSector = () => {
        if (!order?.sector) {
            return [];
        }

        const sector = order?.sector.toLowerCase();

        // If Primary Sector is Europe, show only Europe
        if (sector.includes('europe')) {
            return options.filter((option) => option.value.toLowerCase().includes('europe'));
        }

        // If Primary Sector is APAC, hide Europe and North America
        if (sector.includes('apac')) {
            return options.filter((option) => !option.value.toLowerCase().includes('europe') && !option.value.toLowerCase().includes('north america'));
        }

        // For other sectors, return all options
        return options;
    };

    React.useEffect(() => {
        if (regionApi?.isSuccess) {
            if (regionApi?.data?.data) setOptions(regionApi?.data?.data?.map((app) => ({ label: app, value: app, data: app })));
        }
    }, [regionApi?.isSuccess]);

    React.useEffect(() => {
        if (options && options?.length === 1 && !order?.appRegion) {
            const selectedOption = options[0];
            setOrder({ ...order, appRegion: selectedOption.value, appRegionDetail: selectedOption.data, selectedAppRegionOption: selectedOption });
        }
        if (options && options?.length && order?.appRegion) {
            const appRegion = seldValueExistsInOptions(order?.appRegion);
            if (appRegion && (!order?.selectedAppRegionOption || order?.selectedAppRegionOption?.value !== order?.appRegion)) {
                setOrder({ ...order, appRegion: appRegion.value, appRegionDetail: appRegion.data, selectedAppRegionOption: appRegion });
            }
        }
    }, [options, order?.appRegion]);
    React.useEffect(() => {
        regionApi.mutate();
    }, []);

    React.useEffect(() => {
        if (!order?.appRegion && order?.isTouched) setIsTouched(true);
    }, [order?.appRegion, order?.isTouched]);
    return (
        <Box display="flex" alignItems="center" gap={1}>
            <Select
                key="ApplicationRegion"
                label={<InputLabel label="Application Region" isRequired />}
                value={order?.appRegion || null}
                options={getOptionsBasedOnSector()}
                handleOnSelect={(value) => handleOnSelectChange(value)}
                disabled={regionApi.isLoading}
                isLoading={regionApi.isLoading}
                isRequired={isTouched}
            />
        </Box>
    );
};

export default React.memo(ApplicationRegion);
